(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initReveal = (scope = document) => {
    const items = [...scope.querySelectorAll('[data-ot3-reveal]:not(.is-visible)')];
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    root.classList.add('ot3-motion-ready');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    items.forEach((item, index) => {
      item.style.setProperty('--ot3-reveal-delay', `${Math.min(index % 5, 4) * 70}ms`);
      observer.observe(item);
    });
  };

  const initSpotlights = (scope = document) => {
    if (reduceMotion || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    scope.querySelectorAll('[data-ot3-spotlight]').forEach((card) => {
      if (card.dataset.ot3SpotlightReady) return;
      card.dataset.ot3SpotlightReady = 'true';
      let frame = 0;
      let pointerX = 0;
      let pointerY = 0;
      card.addEventListener('pointermove', (event) => {
        pointerX = event.clientX;
        pointerY = event.clientY;
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          const bounds = card.getBoundingClientRect();
          card.style.setProperty('--ot3-pointer-x', `${pointerX - bounds.left}px`);
          card.style.setProperty('--ot3-pointer-y', `${pointerY - bounds.top}px`);
          frame = 0;
        });
      });
    });
  };

  const initAnchorLinks = (scope = document) => {
    scope.querySelectorAll('a[href*="#Ot3Chooser-model_chooser"], a[href*="#Ot3Steps-saadan_virker_det"]').forEach((anchor) => {
      if (anchor.dataset.ot3AnchorReady) return;
      anchor.dataset.ot3AnchorReady = 'true';
      anchor.addEventListener('click', (event) => {
        const url = new URL(anchor.href, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
        const hash = decodeURIComponent(url.hash.slice(1));
        const target = document.getElementById(hash)
          || document.querySelector(hash.includes('Chooser') ? '[id^="Ot3Chooser-"]' : '[id^="Ot3Steps-"]');
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        window.history.replaceState(null, '', `#${hash}`);
      });
    });
  };

  const initFaqFilter = (scope = document) => {
    scope.querySelectorAll('[data-ot3-faq]').forEach((faq) => {
      if (faq.dataset.ot3FaqReady) return;
      faq.dataset.ot3FaqReady = 'true';

      const buttons = [...faq.querySelectorAll('[data-ot3-faq-filter]')];
      const rows = [...faq.querySelectorAll('[data-ot3-faq-item]')];
      if (!buttons.length || !rows.length) return;

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const filter = button.dataset.ot3FaqFilter;
          buttons.forEach((item) => {
            const active = item === button;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', String(active));
          });

          rows.forEach((row) => {
            const visible = filter === 'all' || row.dataset.ot3FaqCategory === filter;
            row.hidden = !visible;
            if (!visible) row.removeAttribute('open');
          });
        });
      });
    });
  };

  const initChooserTabs = (scope = document) => {
    scope.querySelectorAll('[data-ot3-chooser]').forEach((chooser) => {
      if (chooser.dataset.ot3ChooserReady) return;
      chooser.dataset.ot3ChooserReady = 'true';
      const tabs = [...chooser.querySelectorAll('[data-ot3-chooser-tab]')];
      const cards = [...chooser.querySelectorAll('[data-ot3-model-card]')];
      if (!tabs.length || !cards.length) return;

      const select = (index, focus = false) => {
        tabs.forEach((tab, tabIndex) => {
          const active = tabIndex === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus();
        });
        cards.forEach((card, cardIndex) => {
          const active = cardIndex === index;
          card.classList.toggle('is-active', active);
          card.hidden = window.matchMedia('(max-width: 749px)').matches && !active;
        });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(index));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let target = index;
          if (event.key === 'ArrowLeft') target = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'ArrowRight') target = (index + 1) % tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = tabs.length - 1;
          select(target, true);
        });
      });

      const syncViewport = () => {
        const activeIndex = Math.max(0, tabs.findIndex((tab) => tab.classList.contains('is-active')));
        select(activeIndex);
      };
      window.addEventListener('resize', syncViewport, { passive: true });
      syncViewport();
    });
  };

  const initCarousels = (scope = document) => {
    scope.querySelectorAll('[data-ot3-carousel]').forEach((track) => {
      if (track.dataset.ot3CarouselReady) return;
      track.dataset.ot3CarouselReady = 'true';
      const wrapper = track.closest('[data-ot3-carousel-wrap]') || track.parentElement;
      const cards = [...track.children].filter((item) => item.matches('article, [data-ot3-slide]'));
      const previous = wrapper?.querySelector('[data-ot3-prev]');
      const next = wrapper?.querySelector('[data-ot3-next]');
      const status = wrapper?.querySelector('[data-ot3-status]');
      if (!cards.length) return;

      const currentIndex = () => {
        const left = track.scrollLeft;
        return cards.reduce((best, card, index) =>
          Math.abs(card.offsetLeft - left) < Math.abs(cards[best].offsetLeft - left) ? index : best, 0);
      };
      const update = () => {
        const index = currentIndex();
        if (status) status.textContent = `${index + 1} / ${cards.length}`;
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index === cards.length - 1;
      };
      const go = (direction) => {
        const index = Math.min(cards.length - 1, Math.max(0, currentIndex() + direction));
        cards[index].scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      };
      previous?.addEventListener('click', () => go(-1));
      next?.addEventListener('click', () => go(1));
      track.addEventListener('scroll', update, { passive: true });
      update();
    });
  };

  const initComparisonTabs = (scope = document) => {
    scope.querySelectorAll('[data-ot3-compare]').forEach((comparison) => {
      if (comparison.dataset.ot3CompareReady) return;
      comparison.dataset.ot3CompareReady = 'true';
      const tabs = [...comparison.querySelectorAll('[data-ot3-compare-tab]')];
      const panels = [...comparison.querySelectorAll('[data-ot3-compare-panel]')];
      const select = (index) => {
        tabs.forEach((tab, tabIndex) => {
          const active = tabIndex === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel, panelIndex) => panel.classList.toggle('is-active', panelIndex === index));
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(index));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const target = event.key === 'ArrowRight' ? (index + 1) % tabs.length : (index - 1 + tabs.length) % tabs.length;
          select(target);
          tabs[target].focus();
        });
      });
      if (tabs.length) select(0);
    });
  };

  const initMobileBuybar = () => {
    const bar = document.querySelector('[data-ot3-mobile-buybar]');
    if (!bar || bar.dataset.ot3BuyReady) return;
    bar.dataset.ot3BuyReady = 'true';

    const proxyButton = bar.querySelector('[data-ot3-mobile-buy]');
    const proxyLabel = bar.querySelector('[data-ot3-mobile-buy-label]');
    const proxyPrice = bar.querySelector('[data-ot3-mobile-price]');

    const getNativeButton = () =>
      [...document.querySelectorAll('product-form button[name="add"], product-form .product-form__submit')].find(
        (button) => !button.closest('[data-ot3-mobile-buybar]')
      );

    const sync = () => {
      const nativeButton = getNativeButton();
      if (!nativeButton) return;
      proxyButton.disabled = nativeButton.disabled;
      proxyLabel.textContent = nativeButton.disabled ? 'Udsolgt' : 'Tilføj til kurv';

      const price = document.querySelector(
        '.product__info-container [data-ot3-current-price], .product__info-container .price .price-item--sale, .product__info-container .price .price-item--regular'
      );
      if (price && price.textContent.trim()) proxyPrice.textContent = price.textContent.trim();
    };

    proxyButton.addEventListener('click', () => {
      const nativeButton = getNativeButton();
      if (!nativeButton || nativeButton.disabled) {
        document.querySelector('.product-form__input')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      nativeButton.click();
    });

    const nativeButton = getNativeButton();
    if (nativeButton && 'IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver(
        ([entry]) => bar.classList.toggle('is-hidden', entry.isIntersecting),
        { threshold: 0.45 }
      );
      visibilityObserver.observe(nativeButton);
    }

    const productInfo = document.querySelector('.product__info-container');
    if (productInfo && 'MutationObserver' in window) {
      new MutationObserver(sync).observe(productInfo, { subtree: true, childList: true, attributes: true, characterData: true });
    }
    sync();
  };

  const initHeader = () => {
    const wrapper = document.querySelector('.header-wrapper');
    if (!wrapper || wrapper.dataset.ot3HeaderReady) return;
    wrapper.dataset.ot3HeaderReady = 'true';
    const update = () => wrapper.classList.toggle('ot3-header--scrolled', window.scrollY > 24);
    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  const initDeliveryWindows = (scope = document) => {
    const windows = scope.querySelectorAll('[data-ot3-delivery-window]');
    if (!windows.length) return;

    const now = new Date();
    const weekday = now.getDay();
    const beforeCutoff = weekday > 0 && weekday < 6 && now.getHours() < 14;
    const startInDays = beforeCutoff ? 1 : 2;
    const endInDays = beforeCutoff ? 3 : 4;

    const addBusinessDays = (date, count) => {
      const result = new Date(date);
      let added = 0;
      while (added < count) {
        result.setDate(result.getDate() + 1);
        const day = result.getDay();
        if (day !== 0 && day !== 6) added += 1;
      }
      return result;
    };

    const formatter = new Intl.DateTimeFormat('da-DK', { day: 'numeric', month: 'short' });
    const start = formatter.format(addBusinessDays(now, startInDays));
    const end = formatter.format(addBusinessDays(now, endInDays));
    windows.forEach((item) => { item.textContent = `forventet levering ${start}–${end}`; });

    scope.querySelectorAll('[data-ot3-delivery-cutoff]').forEach((item) => {
      item.textContent = beforeCutoff ? 'Bestil før kl. 14.00' : 'Afsendes næste hverdag';
    });
  };

  const init = (scope = document) => {
    initReveal(scope);
    initSpotlights(scope);
    initAnchorLinks(scope);
    initFaqFilter(scope);
    initChooserTabs(scope);
    initCarousels(scope);
    initComparisonTabs(scope);
    initMobileBuybar();
    initHeader();
    initDeliveryWindows(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('ot3:cart-rendered', (event) => initDeliveryWindows(event.detail?.scope || document));
})();
